import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { Address, OptionKey, PaginationParams } from '@/common/types';
import { API_URL } from '@/dtos';
import { PaymentMethodType } from '@/modules/employee/employee.types';

import { LockboxType } from '../../../Lockbox/lockbox.types';
import { Check, CheckStatus, CheckType, VoidReason } from '../constants';

export interface ChecksQueryParams extends Partial<PaginationParams> {
  statuses?: OptionKey[];
  dateRange?: Array<string | null>;
  organizationId?: string;
  enabled?: boolean;
}

const useGetChecks = ({
  page,
  perPage,
  searchString,
  dateRange,
  statuses,
  organizationId,
  enabled,
}: ChecksQueryParams) => {
  const [dateFrom, dateTo] = dateRange || [];

  const params = {
    size: perPage || 50,
    page,
    ...(organizationId ? { organization_id: organizationId } : {}),
    issued_date_from: dateFrom,
    issued_date_to: dateTo,
    ...(searchString ? { search_value: searchString } : {}),
    ...(statuses?.length
      ? {
          check_status_types: statuses.map((status) => `${status}`.toUpperCase()).join(','),
        }
      : {}),
  };

  const { data, isLoading } = useQuery(
    [API_URL.CHECKS_SEARCH, page, perPage, searchString, organizationId, dateFrom, dateTo, statuses],
    () => (enabled === false ? null : api.get(API_URL.CHECKS_SEARCH, { params }))
  );

  // TODO: not clear which one field is right
  // @ts-ignore
  const total = data?.data?.total_elements || data?.data?.totalElements || 0;

  const checks = useMemo<Check[]>(
    () =>
      (data?.data?.content || []).map(
        (check): Check => ({
          ...(check?.amount && typeof check.amount === 'number' ? { amount: check.amount } : {}),
          ...(check?.check_id ? { id: toString(check.check_id) } : {}),
          ...(check?.claim_ids?.length ? { claimIds: check.claim_ids.map((id) => toString(id)) } : {}),
          ...(check?.lockbox_id ? { lockboxId: toString(check.lockbox_id) } : {}),
          ...(check?.check_type ? { type: check.check_type as unknown as CheckType } : {}),
          ...(check?.current_status ? { status: check.current_status as unknown as CheckStatus } : {}),
          ...(check?.employee_id ? { employeeId: toString(check.employee_id) } : {}),
          ...(check?.employee_name ? { employeeName: check.employee_name } : {}),
          ...(check?.employer_name ? { employerName: check.employer_name } : {}),
          ...(check?.issued_date ? { issuedDate: check.issued_date as unknown as string } : {}),
          ...(check?.pay_to ? { payTo: check.pay_to } : {}),
          ...(check?.status_date ? { statusDate: check.status_date as unknown as string } : {}),
          ...(check?.check_address ? { address: check.check_address as Address } : {}),
          ...(check?.void_reason ? { voidReason: check.void_reason as VoidReason } : {}),
          ...(check?.payment_method_id ? { paymentMethodId: toString(check.payment_method_id) } : {}),
          ...(check?.reissue_check_id ? { reissueCheckId: toString(check.reissue_check_id) } : {}),
          ...(check?.lockbox_type ? { lockboxType: toString(check.lockbox_type) as LockboxType } : {}),
          ...(check?.reissue_payment_method_type
            ? { reissuePaymentMethodType: check.reissue_payment_method_type as unknown as PaymentMethodType }
            : {}),
        })
      ),
    [data]
  );

  return {
    checks,
    isLoading,
    total,
  };
};

export default useGetChecks;
